import React, {FC, useState, useMemo} from 'react';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import {CardProps, withStyles} from '@material-ui/core';

import {getLocale} from '@epic-core/common/dist';
import {compactNumberFormatter} from '@epic-visualizations/charts';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

import StatisticsPanel from '../StatisticsPanel';
import useMyTop10GamesData from './useMyTop10GamesData';
import MyTop10GamesPanelSkeleton from './MyTop10GamesPanelSkeleton';
import CategorySelect from './CategorySelect';
import MyTop10GamesList from './MyTop10GamesList';
import {percentFormatter} from '../../../components/v2/sections/fortnite-games/utils';
import {PERCENT_FORMAT} from '../../../components/v2/sections/fortnite-games/constants';
import {PDFPictureTrap} from '../../v2/sections/fortnite-games/export/PDFExportProvider';
import useDurationFormatter from '../../v2/sections/fortnite-games/hooks/useDurationFormatter';

import {GameDataItem, Option, MyTop10Game} from '../../../types';

const myTop10GamesMap = {
    [MyTop10Game.ACTIVE_PLAYERS]: 'active_players',
    [MyTop10Game.AVERAGE_PLAY_TIME]: 'average_play_time',
    [MyTop10Game.PLAYER_RETENTION]: 'player_retention',
    [MyTop10Game.TOTAL_LIFE_TIME_PLAYS]: 'total_life_time_plays'
};

const StyledPanel = withStyles<string, undefined, CardProps>(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: '600px',
        minWidth: '330px',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper
    }
}))(Card);

const CODES = {
    title: 'epic.affiliate.fortnite_games.sidebar.my_top_10_games.title',
    tooltip: 'epic.affiliate.fortnite_games.sidebar.my_top_10_games.tooltip',
    gameTitle: 'epic.affiliate.fortnite_games.sidebar.my_top_10_games.game_title',

    category: 'epic.affiliate.fortnite_games.sidebar.my_top_10_games.category.',
    categoryDescription:
        'epic.affiliate.fortnite_games.sidebar.my_top_10_games.category_description.',

    dataUnavailableTitle: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_title',
    dataUnavailableDescription: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_description'
};

export interface MyTop10GamesPanelProps {
    getMessage: (code: string) => string;
}

export interface RankedListProps extends GameDataItem {
    rank: number;
    bar: number;
}

const MyTop10GamesPanel: FC<MyTop10GamesPanelProps> = ({getMessage}: MyTop10GamesPanelProps) => {
    const myTop10GamesCategories = Object.values(MyTop10Game);
    const formatDuration = useDurationFormatter();
    const locale = getLocale();

    const formatters = {
        [MyTop10Game.ACTIVE_PLAYERS]: compactNumberFormatter(locale),
        [MyTop10Game.AVERAGE_PLAY_TIME]: formatDuration,
        [MyTop10Game.PLAYER_RETENTION]: percentFormatter(locale, PERCENT_FORMAT),
        [MyTop10Game.TOTAL_LIFE_TIME_PLAYS]: compactNumberFormatter(locale)
    };

    const categoryOptions = useMemo(() => {
        return myTop10GamesCategories.map(value => ({
            label: getMessage(`${CODES.category}${myTop10GamesMap[value]}`),
            value
        }));
    }, []);

    const [activeCategory, setActiveCategory] = useState<Option>(categoryOptions[0]);

    const top10Games = useMyTop10GamesData({
        category: activeCategory.value
    });

    const {list, loading} = top10Games[activeCategory.value];

    const handleCategoryChange = (activeCategory: Option) => {
        setActiveCategory(activeCategory);
    };

    return (
        <PDFPictureTrap id={getMessage(CODES.title)} index={7}>
            <StatisticsPanel
                title={getMessage(CODES.title)}
                tooltip={getMessage(CODES.tooltip)}
                actions={
                    <CategorySelect
                        options={categoryOptions}
                        value={activeCategory}
                        onChange={handleCategoryChange}
                    />
                }>
                <StyledPanel>
                    <Box display="flex" justifyContent="space-between" mx={3} mb={2}>
                        <Typography variant="body2" color="textSecondary">
                            {getMessage(CODES.gameTitle)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {getMessage(
                                `${CODES.categoryDescription}${
                                    myTop10GamesMap[activeCategory.value]
                                }`
                            )}
                        </Typography>
                    </Box>

                    {loading ? (
                        <MyTop10GamesPanelSkeleton />
                    ) : (
                        <MyTop10GamesList
                            list={list}
                            formatter={formatters[activeCategory.value]}
                            tooltipTitle={getMessage(CODES.dataUnavailableTitle)}
                            tooltipDescription={getMessage(CODES.dataUnavailableDescription)}
                        />
                    )}
                </StyledPanel>
            </StatisticsPanel>
        </PDFPictureTrap>
    );
};

export default withLocalization(MyTop10GamesPanel);
